import { Container, DisplayObject, IDestroyOptions, Rectangle, Sprite, Texture } from "pixi.js"
import { TileMap, TileSize } from "game-common/models"
import { BODY_GROUND_OFFSET } from "game-common/lpc/body_placement"
import { HillLevels, hillHeights, hillLevelsOf } from "game-common/room/hill_height"
import { BUILDING_SHADOW } from "./building_shadow"
import { buildingShadeColor, buildingShadeGeneration, buildingShadeStrength, contactShadeOf, inBuildingShade, inContactShade } from "./building_shade"
import { GroundReader, RiseMapSource } from "./ground_rise"
import { contentTexture } from "./content_textures"
import { worldSunDirection } from "./sun"
import { AtlasSlot, SlotAtlas } from "./sprites/slot_atlas"

/**
 * HILLS, DRAWN (openspec world/hills): the room's data-only `hill` layer, a level per cell, turned into terraced grassy
 * ground by the shared height field (game-common room/hill_height.ts) and baked here at runtime. Nothing is moved on
 * the server; the client draws the hill and lifts what stands on it (hill_lift.ts).
 *
 * THE PROJECTION. A ground point (x, y) at height h is drawn at screen (x, y - h): one storey is 32 px. The ground is
 * rasterised north to south in quarter-pixel rows, so a slope falling toward the camera stretches its grass down the
 * screen, and a point further south covers one further north that lands on the same pixel.
 *
 * THE COLOUR of every surface point comes from the ground under it -- the room's own map1 pixels at (x, y), read from
 * the atlas the way ground_rise.ts reads them -- so a slope's toe meets the meadow exactly. It is shaded by the
 * surface's normal against the world sun (toward BUILDING_SHADOW's colour, as the game's shade multiplies), darkened
 * at the toe of a slope (concave) and lit along its lip (convex). Blades of grass hang from the lip of each slope
 * facing the camera and stand up from its toe; the silhouette where a hill hides the ground behind it is lit. All of
 * it is worked out ON THE GROUND from the shared height field, so every chunk and band agrees at its seams.
 *
 * SORTING. A chunk is a square of GROUND, baked as bands of BAND ground rows. Each band holds the surface of its own
 * rows only, drawn where they project (8 rows plus any slope's drop), as one sprite in the world surface sorted at
 * 100 + bandStart - BODY_GROUND_OFFSET - shadowAllowance: the hill draws over a body only where it lies more than the
 * allowance south of the body's soles (a body's own shadow reaches about that far, and the ground it lies on must not
 * cover it), and a band further south draws later, so the hill hides itself with no global pass.
 *
 * SHADE. The buildings' and plants' shade is drawn under the bands, so each band has a twin just over it that shows the
 * shade published for the room (building_shade.ts) where it falls on the band's ground, faded with the daylight. A new
 * publication repaints the twins from the ground points each band keeps, without re-baking its colour.
 *
 * BUDGET. Baking runs a slice at a time -- a strip of a chunk's fields or shade, then one band -- within BAKE_BUDGET_MS
 * a frame, and the room-reveal curtain waits for it (MapRenderingManager.hasPendingRender).
 *
 * Live tuning: window.hill.enabled / .shadowAllowance, or window.hill = false.
 */
export const HILL = { enabled: true, shadowAllowance: 20 }

export const hillConfig = (): typeof HILL => {
    if (typeof window === "undefined") return HILL
    const w = window as any
    if (w.hill === undefined) w.hill = { ...HILL }
    const live = w.hill
    if (live === false) return { ...HILL, enabled: false }
    return live && typeof live === "object" ? { ...HILL, ...live } : HILL
}

/** Ground pixels per side of one chunk (4x4 cells). */
export const HILL_CHUNK = 128
/** Ground rows per band: the unit the hill sorts against bodies in. */
export const HILL_BAND = 8
/** Ground read round a chunk: a blade's reach plus the curvature stencil (3) and the row before a lip fit inside it. */
export const HILL_MARGIN = 12
const MARGIN = HILL_MARGIN
/** How many ground rows a lip's blades reach down the slope, and a toe's up it. */
const LIP_REACH = 8, TOE_REACH = 8
/** Most frame time the bake may take before it waits for the next frame. */
export const BAKE_BUDGET_MS = 4
/** Below this height a point is the meadow's own flat ground (map1 draws it). */
const SURFACE_MIN = 0.25

const LIGHT = [0x80, 0xb2, 0x3e]
const MID = [0x5c, 0x9b, 0x37]
const DARK = [0x2b, 0x6e, 0x2e]
const LIGHT_TIP = LIGHT.map(v => v * 0.9)

const hash01 = (x: number, y: number, salt: number): number => {
    const xx = ((x % 128) + 128) % 128, yy = ((y % 128) + 128) % 128
    let h = (Math.imul(xx, 374761393) + Math.imul(yy, 668265263) + Math.imul(salt, 2246822519)) >>> 0
    h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296
}
const smoothstep = (e0: number, e1: number, x: number) => {
    const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)))
    return t * t * (3 - 2 * t)
}
/** Small-scale clumping for the blades (value noise on a 5px lattice), so they gather in tufts, not a dotted line. */
const clump = (x: number, y: number): number => {
    const u = x / 5, v = y / 5, i = Math.floor(u), j = Math.floor(v)
    let fu = u - i, fv = v - j
    fu = fu * fu * (3 - 2 * fu)
    fv = fv * fv * (3 - 2 * fv)
    const a = hash01(i, j, 9), b = hash01(i + 1, j, 9), c = hash01(i, j + 1, 9), d = hash01(i + 1, j + 1, 9)
    const top = a + (b - a) * fu, bottom = c + (d - c) * fu
    return top + (bottom - top) * fv
}

/** A rectangle of world (ground) pixels. */
export interface GroundRect {
    x: number
    y: number
    w: number
    h: number
}

/**
 * A chunk's surface, worked out on the ground over the chunk grown by MARGIN on every side (row-major): heights, then
 * each point's lip, toe and colour, then the blades. The layer fills it a strip of rows at a time (fillHillHeights,
 * fillHillSurface, fillHillBlades); prepareHillChunk fills it at once.
 */
export interface HillChunkFields {
    surface: "grass" | "sand"
    chunk: GroundRect
    region: GroundRect
    /** The region's map1 pixels (RGBA, row-major over `region`). */
    ground: Uint8ClampedArray
    /** The world sun: the shadow a pixel of height casts, px per px. */
    sun: { x: number; y: number }
    heights: Float32Array
    lipSouth: Float32Array
    toe: Float32Array
    /** RGB of every point of the region (3 a point); only the chunk's own rows are set. */
    colour: Uint8ClampedArray
}

/** One band of a chunk: its ground rows' surface, drawn at screen rows top..top+height. */
export interface HillBand {
    /** The band's first ground row. */
    g0: number
    x: number
    top: number
    width: number
    height: number
    rgba: Uint8ClampedArray
    /** Which quarter-row of the band's ground each pixel shows (1 + q: ground row g0 + q / 4), 0 where it is clear. */
    ground: Uint8Array
}

/** A chunk's fields, nothing yet worked out. */
export const startHillChunk = (ground: Uint8ClampedArray, chunk: GroundRect, sun: { x: number; y: number }, surface: "grass" | "sand" = "grass"): HillChunkFields => {
    const region = { x: chunk.x - MARGIN, y: chunk.y - MARGIN, w: chunk.w + 2 * MARGIN, h: chunk.h + 2 * MARGIN }
    const n = region.w * region.h
    return { surface, chunk, region, ground, sun, heights: new Float32Array(n), lipSouth: new Float32Array(n), toe: new Float32Array(n),
        colour: new Uint8ClampedArray(n * 3) }
}

/** Heights of the region's rows j0..j1 (exactly what one pass over the whole region gives them). */
export const fillHillHeights = (fields: HillChunkFields, levels: HillLevels, j0: number, j1: number): void => {
    const { region } = fields
    if (j1 <= j0) return
    fields.heights.set(hillHeights(levels, { x: region.x, y: region.y + j0, w: region.w, h: j1 - j0 }), j0 * region.w)
}

/** Each point's lip, toe and colour on the region's rows j0..j1; every height must be in. */
export const fillHillSurface = (fields: HillChunkFields, j0: number, j1: number): void => {
    const { chunk, region, ground, sun, heights, lipSouth, toe, colour } = fields
    const { w, h } = region
    // Toward the sun (east, south, up), normalised; flat ground scores 1 against it.
    const lx = -sun.x, ly = -sun.y, ln = Math.sqrt(lx * lx + ly * ly + 1)
    const Lx = lx / ln, Ly = ly / ln, Lz = 1 / ln
    const shade0 = ((BUILDING_SHADOW.color >> 16) & 255) / 255, shade1 = ((BUILDING_SHADOW.color >> 8) & 255) / 255
    const shade2 = (BUILDING_SHADOW.color & 255) / 255
    // The chunk's own rows get colours; lips and toes are found further out, so blades reaching in from just
    // beyond the chunk are drawn here exactly as the neighbouring chunk draws them.
    const c0 = MARGIN, c1 = MARGIN + chunk.h
    const s = 3
    for (let j = Math.max(s, j0); j < Math.min(h - s, j1); j++) {
        const row = j * w
        const ahead3 = Math.min(h - 1, j + 3) * w, ahead5 = Math.min(h - 1, j + 5) * w
        for (let i = s; i < w - s; i++) {
            const k = row + i
            const hh = heights[k]
            const n3 = heights[k - s * w], s3 = heights[k + s * w], w3 = heights[k - s], e3 = heights[k + s]
            // Flat meadow all round: no slope, no lip, no toe, and nothing the hill draws.
            if (hh === 0 && n3 === 0 && s3 === 0 && w3 === 0 && e3 === 0) continue
            const hyy = (s3 + n3 - 2 * hh) / (s * s)
            const hxx = (e3 + w3 - 2 * hh) / (s * s)
            const lip = Math.min(1, Math.max(0, -(hyy + 0.5 * hxx) * 6))
            const toeHere = Math.min(1, Math.max(0, (hyy + hxx) * 4))
            toe[k] = toeHere
            // A lip counts for blades only where the ground just south of it falls toward the camera.
            const ahead = Math.max(0, -(heights[ahead5 + i] - heights[ahead3 + i]) / 2)
            lipSouth[k] = lip * smoothstep(0.08, 0.3, ahead)
            if (j < c0 || j >= c1 || hh <= SURFACE_MIN) continue
            const north = heights[k - w], south = heights[k + w]
            const dx = (heights[k + 1] - heights[k - 1]) / 2, dy = (south - north) / 2
            const illum = ((-dx * Lx) + (-dy * Ly) + Lz) / Math.sqrt(dx * dx + dy * dy + 1) / Lz
            let a = Math.min(1, Math.max(0, (1 - illum) / 0.6)) * 0.7
            a = Math.min(1, a + toeHere * 0.35)
            const lift = Math.min(0.3, Math.max(0, illum - 1)) * 0.7 + lip * 0.22
            const p = k * 4, o = k * 3
            let r = ground[p] * (1 - a + a * shade0) * (1 + lift)
            let g = ground[p + 1] * (1 - a + a * shade1) * (1 + lift)
            let b = ground[p + 2] * (1 - a + a * shade2) * (1 + lift * 0.7)
            // The silhouette: the ground just north of here falls away behind (faces away from the camera) while this
            // point faces it -- the topmost point of the hill in its column of the screen.
            if (hh - north >= 1 && south - hh < 1) {
                if (fields.surface === "sand") {
                    r *= 1.06; g *= 1.06; b *= 1.06
                } else {
                    r = (r + LIGHT[0]) / 2
                    g = (g + LIGHT[1]) / 2
                    b = (b + LIGHT[2]) / 2
                }
            }
            colour[o] = r
            colour[o + 1] = g
            colour[o + 2] = b
        }
    }
}

/**
 * Blades, on the ground: each lip's hang down the slope below it, each toe's stand up the slope above it, both
 * measured in screen rows from where the lip or toe is drawn, picked by world position so every chunk agrees. Every
 * row's surface must be in.
 */
export const fillHillBlades = (fields: HillChunkFields): void => {
    if (fields.surface === "sand") return
    const { chunk, region, heights, lipSouth, toe, colour } = fields
    const { w } = region
    const c0 = MARGIN, c1 = MARGIN + chunk.h
    const paint = (jj: number, i: number, rgb: number[]) => { for (let c = 0; c < 3; c++) colour[(jj * w + i) * 3 + c] = rgb[c] }
    const surface = (jj: number, i: number) => jj >= c0 && jj < c1 && heights[jj * w + i] > SURFACE_MIN
    for (let i = MARGIN; i < MARGIN + chunk.w; i++) {
        const X = region.x + i
        // A lip is found where its row's value crosses from the row above, so both rows must have been worked out.
        for (let j = c0 - LIP_REACH + 1; j < c1 + TOE_REACH - 1; j++) {
            const k = j * w + i, Y = region.y + j
            if (j < c1 && lipSouth[k] > 0.4 && lipSouth[k - w] <= 0.4 && hash01(X, Y, 1) < 0.1 + 0.75 * clump(X, Y) ** 2) {
                const n = 2 + Math.floor(hash01(X, Y, 2) * 5)
                const start = Math.floor(Y - heights[k]) + Math.floor(hash01(X, Y, 3) * 3) - 1
                for (let jj = j; jj < j + LIP_REACH; jj++) {
                    if (!surface(jj, i)) continue
                    const step = Math.floor(region.y + jj - heights[jj * w + i]) - start
                    if (step < 0 || step >= n) continue
                    paint(jj, i, step < 2 ? (hash01(X, Y + step, 5) < 0.5 ? LIGHT : MID) : step < n - 1 ? MID : DARK)
                }
            }
            if (j >= c0 && toe[k] > 0.4 && toe[k + w] <= 0.4 && hash01(X, Y, 6) < 0.05 + 0.6 * clump(X, Y) ** 2) {
                const n = 1 + Math.floor(hash01(X, Y, 7) * 4)
                const bottom = Math.floor(Y - heights[k])
                for (let jj = j; jj > j - TOE_REACH; jj--) {
                    if (!surface(jj, i)) continue
                    const step = bottom - Math.floor(region.y + jj - heights[jj * w + i])
                    if (step < 0 || step >= n) continue
                    paint(jj, i, step === 0 ? DARK : step < n - 1 ? MID : LIGHT_TIP)
                }
            }
        }
    }
}

/** Everything a chunk's bands are drawn from, at once. `ground` is the region's map1 pixels. */
export const prepareHillChunk = (levels: HillLevels, ground: Uint8ClampedArray, chunk: GroundRect,
    sun: { x: number; y: number }, surface: "grass" | "sand" = "grass"): HillChunkFields => {
    const fields = startHillChunk(ground, chunk, sun, surface)
    fillHillHeights(fields, levels, 0, fields.region.h)
    fillHillSurface(fields, 0, fields.region.h)
    fillHillBlades(fields)
    return fields
}

/** The band of `fields`' chunk starting at ground row g0, or undefined when none of it stands above the meadow. */
export const bakeHillBand = (fields: HillChunkFields, g0: number): HillBand | undefined => {
    const { chunk, region, heights, colour } = fields
    const w = region.w
    const rows = Math.min(HILL_BAND, chunk.y + chunk.h - g0)
    const i0 = chunk.x - region.x
    // Where the band's surface lands on the screen.
    let top = Infinity, bottom = -Infinity
    const steps = rows * 4
    for (let q = 0; q < steps; q++) {
        const gy = g0 + q / 4, j = Math.floor(gy) - region.y, f = gy - Math.floor(gy)
        for (let i = 0; i < chunk.w; i++) {
            const k = j * w + i0 + i
            if (heights[k] <= SURFACE_MIN) continue
            const sy = Math.floor(gy - (heights[k] * (1 - f) + heights[k + w] * f))
            if (sy < top) top = sy
            if (sy > bottom) bottom = sy
        }
    }
    if (top === Infinity) return undefined
    const height = bottom - top + 1
    const rgba = new Uint8ClampedArray(chunk.w * height * 4)
    const ground = new Uint8Array(chunk.w * height)
    for (let q = 0; q < steps; q++) {
        const gy = g0 + q / 4, j = Math.floor(gy) - region.y, f = gy - Math.floor(gy)
        for (let i = 0; i < chunk.w; i++) {
            const k = j * w + i0 + i
            if (heights[k] <= SURFACE_MIN) continue
            const sy = Math.floor(gy - (heights[k] * (1 - f) + heights[k + w] * f))
            const o = ((sy - top) * chunk.w + i) * 4
            rgba[o] = colour[k * 3]
            rgba[o + 1] = colour[k * 3 + 1]
            rgba[o + 2] = colour[k * 3 + 2]
            rgba[o + 3] = 255
            ground[o >> 2] = q + 1
        }
    }
    return { g0, x: chunk.x, top, width: chunk.w, height, rgba, ground }
}

/** Ground pixels round a chunk its shade is read for: the cross round a band pixel's ground point reaches one past it. */
const SHADE_PAD = 1
/** Rows of a chunk's shade read in one slice. */
const SHADE_STRIP = 32

/** In the sun's shade (a building's, a plant's), and in the contact shade at the feet of what stands. */
export const SUN_SHADE = 1, CONTACT_SHADE = 2

/** Which ground pixels round a chunk lie in the room's published shade (SUN_SHADE | CONTACT_SHADE), centre by centre. */
export interface HillShadeGrid {
    x: number
    y: number
    w: number
    h: number
    inShade: Uint8Array
    /** Whether any of it is in shade (most of a hill is not: its bands need no shade at all). */
    any: boolean
}

export const startHillShade = (chunk: GroundRect): HillShadeGrid => {
    const w = chunk.w + 2 * SHADE_PAD, h = chunk.h + 2 * SHADE_PAD
    return { x: chunk.x - SHADE_PAD, y: chunk.y - SHADE_PAD, w, h, inShade: new Uint8Array(w * h), any: false }
}

/** Reads rows [row0, row1) of a chunk's shade: `shadeAt` gives SUN_SHADE | CONTACT_SHADE at a ground point. */
export const fillHillShade = (grid: HillShadeGrid, shadeAt: (x: number, y: number) => number, row0: number, row1: number): void => {
    for (let j = row0; j < row1; j++) for (let i = 0; i < grid.w; i++) {
        const shade = shadeAt(grid.x + i + 0.5, grid.y + j + 0.5)
        if (!shade) continue
        grid.inShade[j * grid.w + i] = shade
        grid.any = true
    }
}

/**
 * A band's shade twin (decision 10): over each pixel the band draws, how much darker the shade makes it, as the shade
 * layers would at full strength -- the sun's shade (`colour`) by the share of a cross of ground points round the
 * pixel's own that lie in it, and the contact shade (`contact`, `ratio` times as strong) by its share, multiplied --
 * clear where neither falls. Each pixel holds that darkening D (per channel) as its alpha A = the most of D and a
 * colour of base x (1 - D / A): drawn over the band, normally blended, at the shade's strength `a`, it gives
 * base x (1 - a x D), what the MULTIPLY layers give the meadow (exactly, for the sun's shade alone), with no blend change
 * between bands, so the hill stays one batch. False when none of the band is in shade.
 */
export const paintHillShade = (band: HillBand, grid: HillShadeGrid, colour: number, out: Uint8ClampedArray,
    contact?: { color: number; ratio: number }): boolean => {
    out.fill(0)
    if (!grid.any) return false
    const channel = (c: number, shift: number) => 1 - ((c >> shift) & 0xff) / 255
    const sun = [channel(colour, 16), channel(colour, 8), channel(colour, 0)]
    const touch = contact ? [channel(contact.color, 16), channel(contact.color, 8), channel(contact.color, 0)] : [0, 0, 0]
    const ratio = contact?.ratio ?? 0
    const { rgba, ground, width, g0 } = band
    const c = grid.inShade, w = grid.w
    const darken = [0, 0, 0]
    let shaded = false
    for (let p = 0; p < ground.length; p++) {
        const v = ground[p]
        if (!v) continue
        const k = (g0 + ((v - 1) >> 2) - grid.y) * w + band.x + (p % width) - grid.x
        const a = c[k], b = c[k - 1], d = c[k + 1], e = c[k - w], f = c[k + w]
        if (!(a | b | d | e | f)) continue
        const inSun = ((a & SUN_SHADE) + (b & SUN_SHADE) + (d & SUN_SHADE) + (e & SUN_SHADE) + (f & SUN_SHADE)) / 5
        const inContact = Math.min(1, ((a >> 1) + (b >> 1) + (d >> 1) + (e >> 1) + (f >> 1)) / 5 * ratio)
        let most = 0
        for (let ch = 0; ch < 3; ch++) {
            darken[ch] = 1 - (1 - inSun * sun[ch]) * (1 - inContact * touch[ch])
            if (darken[ch] > most) most = darken[ch]
        }
        if (most <= 0) continue
        shaded = true
        const o = p * 4
        for (let ch = 0; ch < 3; ch++) out[o + ch] = rgba[o + ch] * (1 - darken[ch] / most)
        out[o + 3] = Math.round(most * 255)
    }
    return shaded
}

/** Where a band sorts among bodies (whose zIndex is 100 + hip y; soles = hip + BODY_GROUND_OFFSET). */
export const hillBandZIndex = (g0: number, shadowAllowance = hillConfig().shadowAllowance): number =>
    100 + g0 - BODY_GROUND_OFFSET - shadowAllowance

/** A whole chunk's bands at once (tests and tools; the layer bakes them a slice at a time). */
export const bakeHillChunk = (levels: HillLevels, ground: Uint8ClampedArray, chunk: GroundRect,
    sun: { x: number; y: number }): HillBand[] => {
    const fields = prepareHillChunk(levels, ground, chunk, sun)
    const bands: HillBand[] = []
    for (let g0 = chunk.y; g0 < chunk.y + chunk.h; g0 += HILL_BAND) {
        const band = bakeHillBand(fields, g0)
        if (band) bands.push(band)
    }
    return bands
}

/** What the layer needs from a room's tile map. */
export interface HillMapSource extends RiseMapSource {
    getHillSurface?(): "sand" | undefined
    getHillLayer?(): TileMap | undefined
    shouldDrawTileAt?(x: number, y: number, layerId: string, tilemap: TileMap): boolean
    isHiddenTile?(tile: string): boolean
    getCompositeTileId?(tile: string): string | undefined
}

/** The flat layers a hill's surface carries, bottom first: the ground, then what lies on it (flowers, dirt, floors). */
export const HILL_FLAT_LAYERS = ["map1", "map2", "map3"]

/**
 * The ground a hill is made of, as the flat tile layers draw it: map1's grass and soil with map2's and map3's flowers,
 * dirt edges and floors laid over it, every tile the map would draw flat -- not a composite (a plant: a sprite of its
 * own, lifted by the composite renderer), not a hidden tile. Undefined until every atlas is decoded.
 */
export const readHillGround: GroundReader = (source: HillMapSource, px: number, py: number, w: number, h: number) => {
    if (typeof document === "undefined") return undefined
    const canvas = document.createElement("canvas")
    canvas.width = w
    canvas.height = h
    const context = canvas.getContext("2d")
    if (!context) return undefined
    for (const layerId of HILL_FLAT_LAYERS) {
        const layer = source.resolveTileMapFromLayerId(layerId)
        if (!layer) continue
        for (let cy = Math.floor(py / TileSize); cy * TileSize < py + h; cy++) {
            for (let cx = Math.floor(px / TileSize); cx * TileSize < px + w; cx++) {
                const tile = layer[cx]?.[cy]
                if (!tile || tile === "0" || !flatTileDrawn(source, layerId, layer, tile, cx, cy)) continue
                const texture = contentTexture(source.resolveTilesetTileId(tile))
                const image = (texture?.baseTexture?.resource as any)?.source
                if (!image || !texture.baseTexture.valid) return undefined
                const frame = texture.frame
                context.drawImage(image, frame.x, frame.y, frame.width, frame.height,
                    cx * TileSize - px, cy * TileSize - py, TileSize, TileSize)
            }
        }
    }
    try {
        return context.getImageData(0, 0, w, h).data
    } catch {
        return undefined // a tainted canvas: no hill rather than a failed map
    }
}

const flatTileDrawn = (source: HillMapSource, layerId: string, layer: TileMap, tile: string, x: number, y: number): boolean =>
    (source.shouldDrawTileAt?.(x, y, layerId, layer) ?? true) && !source.isHiddenTile?.(tile) && !source.getCompositeTileId?.(tile)

interface Placed {
    sprite: Sprite
    band: HillBand
    /** The band's shade twin, just over it (paintHillShade), and its pixels. */
    shade: Sprite
    shadeRgba: Uint8ClampedArray
    /** Where the twin's pixels sit on the chunk's shelf. */
    shadeAt: ShelfPlace
    /** Whether any of the band is in shade (else the twin is hidden and its pixels clear). */
    shaded: boolean
}

interface ShelfPlace {
    slot: AtlasSlot
    y: number
}

interface HillChunk {
    key: string
    x: number
    y: number
    baked: string
    placed: Placed[]
    slots: AtlasSlot[]
    /** Rows of the last slot already used by earlier bands (a shelf: bands stack down each slot). */
    shelf: number
    /** The building shade (buildingShadeGeneration) its bands' twins show; -1 before any. */
    shaded: number
}

/** Rows of the region a heights or surface slice works on: small enough that a slice stays well inside the budget. */
const STRIP = 8

interface Work {
    chunk: HillChunk
    signature: string
    fields?: HillChunkFields
    /** Where the chunk's bake has got to: heights, the surface, its shade, the blades, then a band at a time. */
    stage: "heights" | "surface" | "shade" | "blades" | "bands"
    /** The next row (heights, surface, shade) or band (bands) to do. */
    next: number
    /** The building shade round the chunk, read in the shade stage, and which publication of it that was. */
    shade?: HillShadeGrid
    generation?: number
}

/** Repainting a baked chunk's shade twins for a new publication of the room's shade: its shade, then a band at a time. */
interface ShadeWork {
    chunk: HillChunk
    generation: number
    grid: HillShadeGrid
    stage: "grid" | "bands"
    next: number
}

/**
 * The room's hill, as band sprites in the world surface (this container's parent). sync() after every full map render
 * decides which chunks exist and which changed (levels, the ground under them, the config); tick() bakes the waiting
 * ones a slice at a time within the frame budget.
 */
export class HillLayer extends Container {
    private source?: HillMapSource
    private levels?: HillLevels
    private chunks = new Map<string, HillChunk>()
    private slots = new SlotAtlas(HILL_CHUNK)
    private queue: Work[] = []
    private shading?: ShadeWork
    /** The shade's strength the twins show (buildingShadeStrength: the daylight's). */
    private shadeAlpha = 0
    private configKey = ""
    /** How long the longest recent slice took: a slice is started only when one this long still fits the budget. */
    private sliceMs = 0
    /** The longest slice ever, and how many slices have run (debugging; tests drive a clock off the count). */
    longestSliceMs = 0
    slices = 0

    constructor(private readGround: GroundReader = readHillGround, private now: () => number = () => performance.now()) {
        super()
        this.interactiveChildren = false
    }

    sync = (source: HillMapSource | undefined): void => {
        const config = hillConfig()
        this.configKey = `${config.enabled}|${config.shadowAllowance}`
        const levels = hillLevelsOf(source?.getHillLayer?.())
        if (!source || !levels || !config.enabled) {
            this.source = source
            this.levels = undefined
            this.clearChunks()
            return
        }
        if (source.roomId !== this.source?.roomId) this.clearChunks()
        this.source = source
        this.levels = levels
        const per = HILL_CHUNK / TileSize
        // Chunks a hill cell, or the slope's foot past one (under a cell), reaches into.
        const wanted = new Map<string, { x: number; y: number }>()
        for (let cy = 0; cy < levels.height; cy++) for (let cx = 0; cx < levels.width; cx++) {
            if (!levels.at(cx, cy)) continue
            for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
                const x = Math.floor((cx + dx) / per), y = Math.floor((cy + dy) / per)
                if (x >= 0 && y >= 0) wanted.set(`${x},${y}`, { x, y })
            }
        }
        this.chunks.forEach((chunk, key) => { if (!wanted.has(key)) this.dropChunk(chunk) })
        const flat = HILL_FLAT_LAYERS.map(layerId => source.resolveTileMapFromLayerId(layerId))
        const sun = worldSunDirection()
        this.queue = []
        wanted.forEach(({ x, y }, key) => {
            // Everything a chunk is baked from: the levels in reach, the flat tiles under it, the sun and the config.
            const parts = [this.configKey, sun.x, sun.y, source.getHillSurface?.() ?? "grass"]
            for (let cy = y * per - 2; cy < (y + 1) * per + 2; cy++) {
                for (let cx = x * per - 2; cx < (x + 1) * per + 2; cx++) {
                    parts.push(`${levels.at(cx, cy)}${flat.map(layer => layer?.[cx]?.[cy] ?? "").join("/")}`)
                }
            }
            const signature = parts.join(",")
            const chunk = this.chunks.get(key) ?? { key, x, y, baked: "", placed: [], slots: [], shelf: 0, shaded: -1 }
            this.chunks.set(key, chunk)
            if (chunk.baked !== signature) this.queue.push({ chunk, signature, stage: "heights", next: 0 })
        })
    }

    /** Bake waiting slices until the next one would not fit the frame budget. */
    tick = (budgetMs: number = BAKE_BUDGET_MS): void => {
        if (!this.source) return
        const config = hillConfig()
        if (`${config.enabled}|${config.shadowAllowance}` !== this.configKey) this.sync(this.source)
        this.followDaylight()
        if (!this.levels || !this.parent) return
        const start = this.now()
        let slices = 0
        for (;;) {
            // Bakes first; then the twins of baked chunks whose shade has changed.
            const work = this.queue[0], shading = work ? undefined : this.nextShade()
            if (!work && !shading) return
            // Always one slice a frame, so a slow machine still finishes; after that, only what still fits.
            if (slices++ && this.now() - start + this.sliceMs > budgetMs) return
            const sliceStart = this.now()
            if (work && !this.slice(work)) return // an atlas still decoding: try again next frame
            if (shading) this.shadeSlice(shading)
            this.slices++
            const took = this.now() - sliceStart
            this.sliceMs = Math.max(this.sliceMs * 0.9, took)
            this.longestSliceMs = Math.max(this.longestSliceMs, took)
        }
    }

    /**
     * One slice of a chunk's bake -- a strip of its heights, a strip of its surface, its blades, or one band -- false
     * when the ground under it cannot be read yet.
     */
    private slice = (work: Work): boolean => {
        const { chunk } = work
        const rect = { x: chunk.x * HILL_CHUNK, y: chunk.y * HILL_CHUNK, w: HILL_CHUNK, h: HILL_CHUNK }
        if (!work.fields) {
            const ground = this.readRegion(rect)
            if (!ground) return false
            work.fields = startHillChunk(ground, rect, worldSunDirection(), this.source.getHillSurface?.() ?? "grass")
        }
        const fields = work.fields
        const rows = fields.region.h
        switch (work.stage) {
            case "heights":
                fillHillHeights(fields, this.levels!, work.next, Math.min(rows, work.next + STRIP))
                work.next += STRIP
                if (work.next >= rows) { work.stage = "surface"; work.next = 0 }
                return true
            case "surface":
                fillHillSurface(fields, work.next, Math.min(rows, work.next + STRIP))
                work.next += STRIP
                if (work.next >= rows) { work.stage = "shade"; work.next = 0 }
                return true
            case "shade": {
                if (!work.shade) {
                    work.shade = startHillShade(rect)
                    work.generation = buildingShadeGeneration()
                }
                fillHillShade(work.shade, this.inShade, work.next, Math.min(work.shade.h, work.next + SHADE_STRIP))
                work.next += SHADE_STRIP
                if (work.next >= work.shade.h) { work.stage = "blades"; work.next = 0 }
                return true
            }
            case "blades":
                fillHillBlades(fields)
                // The old bands go as the new ones are about to be drawn, so a re-bake never shows a gap for long.
                this.emptyChunk(chunk)
                work.stage = "bands"
                return true
            case "bands": {
                const band = bakeHillBand(fields, rect.y + work.next * HILL_BAND)
                if (band) this.place(chunk, band, work.shade)
                work.next++
                if (work.next * HILL_BAND >= HILL_CHUNK) {
                    chunk.baked = work.signature
                    // A newer publication since the shade was read: the twins repaint (nextShade).
                    chunk.shaded = work.generation ?? -1
                    this.queue.shift()
                }
                return true
            }
        }
    }

    /** The map1 pixels of a chunk and its margin, padded with nothing past the map's edge. */
    private readRegion = (rect: GroundRect): Uint8ClampedArray | undefined => {
        const source = this.source!
        const region = { x: rect.x - MARGIN, y: rect.y - MARGIN, w: rect.w + 2 * MARGIN, h: rect.h + 2 * MARGIN }
        const rx = Math.max(0, region.x), ry = Math.max(0, region.y)
        const rw = Math.min(source.width() * TileSize, region.x + region.w) - rx
        const rh = Math.min(source.height() * TileSize, region.y + region.h) - ry
        if (rw <= 0 || rh <= 0) return new Uint8ClampedArray(region.w * region.h * 4)
        const pixels = this.readGround(source, rx, ry, rw, rh)
        if (!pixels) return undefined
        const padded = new Uint8ClampedArray(region.w * region.h * 4)
        for (let y = 0; y < rh; y++) {
            padded.set(pixels.subarray(y * rw * 4, (y + 1) * rw * 4), ((y + ry - region.y) * region.w + rx - region.x) * 4)
        }
        return padded
    }

    /** Paint a band onto the chunk's shelf and show it in the world surface, sorted by its ground rows; its twin just over it. */
    private place = (chunk: HillChunk, band: HillBand, shade: HillShadeGrid | undefined): void => {
        const surface = this.parent
        if (!surface) return
        const at = this.shelve(chunk, band.height), shadeAt = this.shelve(chunk, band.height)
        const context = at && this.slots.context(at.slot)
        if (!at || !shadeAt || !context) return
        context.putImageData(new ImageData(band.rgba, band.width, band.height), at.slot.x, at.slot.y + at.y)
        this.slots.touch(at.slot)
        const sprite = this.spriteAt(at, band)
        sprite.zIndex = hillBandZIndex(band.g0)
        const twin = this.spriteAt(shadeAt, band)
        twin.zIndex = sprite.zIndex + 0.01
        twin.alpha = this.shadeAlpha
        twin.visible = false
        const placed: Placed = { sprite, band, shade: twin, shadeRgba: new Uint8ClampedArray(band.rgba.length), shadeAt, shaded: false }
        this.paintShade(placed, shade)
        surface.addChild(sprite, twin)
        chunk.placed.push(placed)
    }

    /** Room for `height` rows on the chunk's shelf, on a new slot when the last one is full. */
    private shelve = (chunk: HillChunk, height: number): ShelfPlace | undefined => {
        let slot = chunk.slots[chunk.slots.length - 1]
        if (!slot || chunk.shelf + height > HILL_CHUNK) {
            slot = this.slots.claim()
            if (!slot) return undefined
            chunk.slots.push(slot)
            chunk.shelf = 0
        }
        const y = chunk.shelf
        chunk.shelf += height
        return { slot, y }
    }

    private spriteAt = (at: ShelfPlace, band: HillBand): Sprite => {
        const sprite = new Sprite(new Texture(at.slot.texture.baseTexture, new Rectangle(at.slot.x, at.slot.y + at.y, band.width, band.height)))
        sprite.position.set(band.x, band.top)
        return sprite
    }

    private inShade = (x: number, y: number): number =>
        (inBuildingShade(this.source?.roomId, x, y) ? SUN_SHADE : 0) | (inContactShade(this.source?.roomId, x, y) ? CONTACT_SHADE : 0)

    /** Repaint a band's twin from the shade round its chunk (none: clear). */
    private paintShade = (placed: Placed, grid: HillShadeGrid | undefined): void => {
        const { band, shadeRgba, shadeAt } = placed
        const roomId = this.source?.roomId
        const shaded = !!grid && paintHillShade(band, grid, buildingShadeColor(roomId), shadeRgba, contactShadeOf(roomId))
        if (!shaded && !placed.shaded) return // clear before, clear now
        if (!shaded) shadeRgba.fill(0)
        placed.shaded = shaded
        placed.shade.visible = shaded
        const context = this.slots.context(shadeAt.slot)
        if (!context) return
        context.putImageData(new ImageData(shadeRgba, band.width, band.height), shadeAt.slot.x, shadeAt.slot.y + shadeAt.y)
        this.slots.touch(shadeAt.slot)
    }

    /** The twins fade with the shade on the meadow, by the daylight (MapRenderingManager publishes its strength). */
    private followDaylight = (): void => {
        const strength = buildingShadeStrength(this.source?.roomId) ?? 0
        if (strength === this.shadeAlpha) return
        this.shadeAlpha = strength
        this.chunks.forEach(chunk => chunk.placed.forEach(({ shade }) => { shade.alpha = strength }))
    }

    private queued = (chunk: HillChunk): boolean => this.queue.some(work => work.chunk === chunk)

    /** The baked chunk whose twins show an older publication of the room's shade than the latest, if any. */
    private nextShade = (): ShadeWork | undefined => {
        const generation = buildingShadeGeneration()
        const current = this.shading
        if (current && current.generation === generation && this.chunks.get(current.chunk.key) === current.chunk &&
            !this.queued(current.chunk)) return current
        this.shading = undefined
        for (const chunk of this.chunks.values()) {
            if (chunk.shaded === generation || this.queued(chunk)) continue
            if (!chunk.placed.length) { chunk.shaded = generation; continue }
            const grid = startHillShade({ x: chunk.x * HILL_CHUNK, y: chunk.y * HILL_CHUNK, w: HILL_CHUNK, h: HILL_CHUNK })
            return (this.shading = { chunk, generation, grid, stage: "grid", next: 0 })
        }
        return undefined
    }

    /** One slice of a twin repaint: a strip of the chunk's shade, or one band's twin. */
    private shadeSlice = (work: ShadeWork): void => {
        if (work.stage === "grid") {
            fillHillShade(work.grid, this.inShade, work.next, Math.min(work.grid.h, work.next + SHADE_STRIP))
            work.next += SHADE_STRIP
            if (work.next >= work.grid.h) { work.stage = "bands"; work.next = 0 }
            return
        }
        const placed = work.chunk.placed[work.next++]
        if (placed) this.paintShade(placed, work.grid)
        if (work.next >= work.chunk.placed.length) {
            work.chunk.shaded = work.generation
            this.shading = undefined
        }
    }

    private emptyChunk = (chunk: HillChunk): void => {
        chunk.placed.forEach(({ sprite, shade }) => {
            sprite.destroy({ texture: true })
            shade.destroy({ texture: true })
        })
        chunk.placed = []
        chunk.slots.forEach(slot => this.slots.release(slot))
        chunk.slots = []
        chunk.shelf = 0
    }

    private dropChunk = (chunk: HillChunk): void => {
        this.emptyChunk(chunk)
        this.chunks.delete(chunk.key)
    }

    private clearChunks = (): void => {
        this.chunks.forEach(chunk => this.emptyChunk(chunk))
        this.chunks.clear()
        this.slots.reset()
        this.queue = []
        this.shading = undefined
    }

    /** The band sprites: they live in the world surface, beside this layer, and travel with it on a role change. */
    displayObjects = (): DisplayObject[] =>
        [...this.chunks.values()].flatMap(chunk => chunk.placed.flatMap(({ sprite, shade }) => [sprite, shade]))

    /** Every band baked so far, with the zIndex it sorts at and its shade twin (tests and debugging). */
    bands = (): { band: HillBand; zIndex: number; shade: { rgba: Uint8ClampedArray; alpha: number; visible: boolean; zIndex: number } }[] =>
        [...this.chunks.values()].flatMap(chunk => chunk.placed.map(({ sprite, band, shade, shadeRgba }) =>
            ({ band, zIndex: sprite.zIndex, shade: { rgba: shadeRgba, alpha: shade.alpha, visible: shade.visible, zIndex: shade.zIndex } })))

    /** True while this room has a hill (its chunks are synced). */
    get active(): boolean {
        return !!this.levels
    }

    /** True while chunks are still waiting to bake, or their twins to show the latest shade. */
    get baking(): boolean {
        return this.queue.length > 0 || (!!this.levels && !!this.nextShade())
    }

    destroy(options?: IDestroyOptions | boolean): void {
        this.clearChunks()
        super.destroy(options)
    }
}
